import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Select } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw as FieldContainerIwBase } from '@/_pages/interwill/components/FieldContainerIw';
import { useI18nValues } from '@/entities/personalIdentifiers/hooks/useI18nValues';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { SelectGender } from '@/shared/components/forms/fields/SelectGender';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { CreatePersonalIdentifierProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { NewPidModalParams } from './types';

export const CreatePersonalIdentifierModal: FC<CreatePersonalIdentifierProps> = ({
  beneficiaryId,
  country,
  onSubmit,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'createPid' });

  const sjDatabase = useSJDatabase();
  const { keyPair } = useKeyPairStore();

  const onClose = useModalsStore((s) => s.closeModal);
  const { type } = useI18nValues();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm<NewPidModalParams>({
    defaultValues: { country },
  });

  const onSubmitHandler = async (values: NewPidModalParams) => {
    const id = sjDatabase.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS);

    const newDto = {
      id: id,
      type: DocumentType.PERSONAL_IDENTIFIERS,
      personalIdentifierType: values.idType,
      dateOfIssue: values.startDate,
      number: values.documentNumber,
      citizenship: values.country,
      country: values.country,
      name: values.title,
      beneficiaryId: beneficiaryId ?? 'me',
      names: values.firstName,
      surnames: values.lastName,
      secondName: values.secondName,
      gender: values.gender,
      birthDate: values.dateOfBirth,
    };

    const pid = await sjDatabase.collections.personalIdentifiersDocuments?.create(newDto, [
      keyPair?.publicKey || '',
    ]);

    onClose();
    onSubmit(pid?.id);
  };

  const isPassport = watch('idType') === PersonalIdentifierType.PASSPORT;

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmitHandler)}>
        <InnerForm>
          <Controller
            name={'idType'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Select
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                      options={[
                        PersonalIdentifierType.PASSPORT,
                        PersonalIdentifierType.NATIONAL_ID,
                      ].map((item) => ({
                        label: type(item),
                        value: item,
                      }))}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
          <Separator />

          <Controller
            name={'title'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Row>
            <Controller
              name={'firstName'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'lastName'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
          <Row>
            <Controller
              name={'secondName'}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                    <Form.Item help={fieldState.error?.message}>
                      <Input
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        type={'text'}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
          <Row>
            <Controller
              name={'dateOfBirth'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <DatePicker
                        format={'DD MMMM YYYY'}
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'gender'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectGender
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </Row>
          <Controller
            name={'country'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <SelectCountry placeholder={t(`fields.${field.name}.placeholder`)} {...field} />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'documentNumber'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw
                  label={t(`fields.${isPassport ? 'passportNumber' : 'documentNumber'}.label`)}
                  required
                >
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={t(
                        `fields.${isPassport ? 'passportNumber' : 'documentNumber'}.placeholder`,
                      )}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />

          <Controller
            name={'startDate'}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker
                      format={'DD MMMM YYYY'}
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 420px;
  overflow: auto;
  padding: 0 2px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

const FieldContainerIw = styled(FieldContainerIwBase)`
  flex: 0 50%;
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: ${COLORS.systemDark100};
  flex-shrink: 0;
`;
